import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import './ImageViewer.css';

const ImageViewer = ({ isOpen, onClose, imageUrl, title, artist }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // track pan position
  const lastPosRef = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsLoading(true);
      setScale(1);
      setPos({ x: 0, y: 0 });
      lastPosRef.current = { x: 0, y: 0 };
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, onClose]);

  // Zoom with mouse wheel
  const onWheel = useCallback((e) => {
    e.preventDefault();
    let newScale = scale + (e.deltaY < 0 ? 0.1 : -0.1);
    newScale = Math.min(Math.max(newScale, 1), 5);
    setScale(newScale);
    // Reset pan position when scale is 1
    if (newScale === 1) {
      setPos({ x: 0, y: 0 });
      lastPosRef.current = { x: 0, y: 0 };
    }
  }, [scale]);

  // Mouse drag start
  const onMouseDown = (e) => {
    if (scale === 1) return; // no pan if not zoomed
    e.preventDefault();
    draggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  // Mouse drag move
  const onMouseMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    const newPos = {
      x: lastPosRef.current.x + dx,
      y: lastPosRef.current.y + dy,
    };
    setPos(newPos);
  };

  // Mouse drag end
  const onMouseUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    lastPosRef.current = pos;
  };

  // Touch start
  const onTouchStart = (e) => {
    if (scale === 1) return;
    if (e.touches.length === 1) {
      draggingRef.current = true;
      dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else {
        draggingRef.current = false; // Cancel pan if multi-touch
    }
  };

  // Touch move
  const onTouchMove = (e) => {
    if (!draggingRef.current) return;
    if (e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - dragStartRef.current.x;
    const dy = e.touches[0].clientY - dragStartRef.current.y;
    const newPos = {
      x: lastPosRef.current.x + dx,
      y: lastPosRef.current.y + dy,
    };
    setPos(newPos);
  };

  // Touch end
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    lastPosRef.current = pos;
  };

  if (!isOpen) return null;

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-backdrop" />

      <div
        className="image-viewer-container"
        onClick={e => e.stopPropagation()}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          className="image-viewer-close-btn"
          onClick={onClose}
          aria-label="Close image viewer"
          title="Close (ESC)"
        >
          <X size={28} />
        </button>

        <div className="image-viewer-content">
          {isLoading && <div className="image-viewer-loading">Loading...</div>}
          <img
            src={imageUrl}
            alt={title}
            className="image-viewer-image"
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
            draggable={false}
            style={{
              transform: `scale(${scale}) translate(${pos.x / scale}px, ${pos.y / scale}px)`,
              cursor: scale > 1 ? 'grab' : 'auto',
            }}
          />
        </div>

        {title && artist && (
          <div className="image-viewer-info">
            <h2 className="image-viewer-title">{title}</h2>
            <p className="image-viewer-artist">by {artist}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageViewer;
